import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { getSalesReportData, formatDateForApi } from '../../utilities/api';

const isPlainObject = (v) => v !== null && typeof v === 'object' && !Array.isArray(v);

// The fixed total fields we always append after the payment-mode columns. If a
// payment mode from the API happened to share one of these names (e.g. a POS that
// records "NC" as a settlement mode, not just as our nc_sales category total), JS
// object key order keeps that key at its *first* insertion spot even once we later
// reassign it — so it'd land among the modes instead of after Actual Sales. Object.keys()
// order is what the summary tiles render in (the table is immune, since it builds its
// column list from an explicit array), so this collision has to be prevented at the source.
const RESERVED_TOTAL_FIELDS = new Set(['ACTUAL_SALES', 'NC', 'NC_QTY', 'EXPECTED_SALES', 'CANCELLED', 'DISCOUNT_AMOUNT', 'DISCOUNT_QTY', 'TOTAL']);

/**
 * The payment_bydaterannge response nests four sales categories per date:
 * actual_sales / nc_sales / credit_sales / cancelled_sales, each { total, Cash, Card, ... },
 * plus a flat expected_sales number. The report table shows one column per payment
 * mode (Cash/Card/Paytm/...) carrying the *actual* sales amount, followed by fixed
 * total columns: Actual Sales, NC, Expected Sales (= Actual + NC), Cancelled,
 * Total (= Expected + Cancelled). Credit isn't shown as its own column — it only
 * surfaces in the mode-cell tooltip alongside NC/Cancelled for that payment mode.
 */
const buildSalesReportView = (bills, rawSummary) => {
    const rows = [...(Array.isArray(bills) ? bills : []), rawSummary].filter(Boolean);

    // Discover payment modes from whichever category has them, so a mode that's
    // zero/absent in actual_sales but present in nc/credit/cancelled still gets picked up.
    const modeSet = new Set();
    rows.forEach((row) => {
        ['actual_sales', 'nc_sales', 'credit_sales', 'cancelled_sales'].forEach((catKey) => {
            const cat = row?.[catKey];
            if (isPlainObject(cat)) {
                Object.keys(cat).forEach((k) => {
                    if (k.toLowerCase() === 'total') return;
                    if (RESERVED_TOTAL_FIELDS.has(k.toUpperCase())) return;
                    modeSet.add(k);
                });
            }
        });
    });
    const paymentModes = Array.from(modeSet);

    const buildRow = (row) => {
        const actual = isPlainObject(row.actual_sales) ? row.actual_sales : {};
        const nc = isPlainObject(row.nc_sales) ? row.nc_sales : {};
        const credit = isPlainObject(row.credit_sales) ? row.credit_sales : {};
        const cancelled = isPlainObject(row.cancelled_sales) ? row.cancelled_sales : {};

        const out = {};
        if (row.payment_date !== undefined) out.payment_date = row.payment_date;

        const modeTooltips = {};
        paymentModes.forEach((mode) => {
            out[mode] = actual[mode] ?? 0;
            modeTooltips[mode] = { NC: nc[mode] ?? 0, Credit: credit[mode] ?? 0, Cancelled: cancelled[mode] ?? 0 };
        });
        out._modeTooltips = modeTooltips;

        const actualTotal = actual.total ?? actual.Total ?? 0;
        const ncTotal = nc.total ?? nc.Total ?? 0;
        const ncQty = nc.qty ?? 0;
        const cancelledTotal = cancelled.total ?? cancelled.Total ?? 0;
        const expectedSales = row.expected_sales ?? actualTotal + ncTotal;

        // `delete` before (re)assigning: reassigning an existing key keeps its original
        // key-order position, so if a mode's name ever collided with one of these
        // (belt-and-suspenders — the modeSet filter above already excludes them),
        // deleting first guarantees these fields always end up ordered last, with
        // Total genuinely last.
        ['Actual_Sales', 'NC', 'NC_Qty', 'Expected_Sales', 'Cancelled', 'Discount_Amount', 'Discount_Qty', 'Total'].forEach((f) => delete out[f]);
        out.Actual_Sales = actualTotal;
        out.NC = ncTotal;
        out.NC_Qty = ncQty;
        out.Expected_Sales = expectedSales;
        out.Cancelled = cancelledTotal;
        out.Discount_Amount = row.discount_amount ?? 0;
        out.Discount_Qty = row.discount_qty ?? 0;
        out.Total = expectedSales + cancelledTotal;

        return out;
    };

    const flatBills = (Array.isArray(bills) ? bills : []).map(buildRow);

    const summaryRow = buildRow(rawSummary || {});
    const flatSummary = {};
    Object.entries(summaryRow).forEach(([key, value]) => {
        if (key === '_modeTooltips') return;
        flatSummary[`Total_${key}`] = value;
    });

    // Tiles show the same set of totals as the table's trailing columns; the
    // per-mode NC/Credit/Cancelled breakdown (summaryRow._modeTooltips) is kept
    // separately so the summary's Cash/Card/... rows can show the same hover
    // tooltip the table's mode cells show, just aggregated over the whole range.
    const summaryCards = { ...flatSummary };
    const summaryModeTooltips = summaryRow._modeTooltips || {};

    return { flatBills, flatSummary, summaryCards, summaryModeTooltips, paymentModes };
};

// Async thunk to fetch sales report data
export const fetchSalesReportData = createAsyncThunk(
    'salesReports/fetchSalesReportData',
    async ({ fromDate, toDate, branchId }, { rejectWithValue }) => {
        try {
            const resolvedBranchId = branchId || 'All';
            const clientId = localStorage.getItem('clientid') || '';
            const rolename = localStorage.getItem('role') || '';
            const payload = {
                from_date: fromDate ? formatDateForApi(fromDate) : '',
                to_date: toDate ? formatDateForApi(toDate) : '',
                branch_id: resolvedBranchId,
                rolename,
                clientname: clientId,
                client_id: clientId
            };
            const data = await getSalesReportData(payload);
            if (data?.error) {
                return rejectWithValue(data.error);
            }
            return data;
        } catch (error) {
            return rejectWithValue(error.message || 'Failed to fetch sales report data');
        }
    }
);

const initialState = {
    // Selected filters
    selectedBranch: null,
    fromDate: null,
    toDate: null,

    // Snapshot of the filters as of the last Apply click. Branch Analysis (and
    // anything else that shouldn't refetch on every date-picker keystroke) reads
    // these instead of the live filters above, which update on every onChange.
    appliedFromDate: null,
    appliedToDate: null,
    appliedBranch: null,

    // Sales data
    salesData: [],
    summary: {},
    summaryCards: {},
    summaryModeTooltips: {},
    paymentModes: [],
    orgName: '',
    branchTitle: '',

    // Loading states
    loading: false,
    
    // Error states
    error: null
};

const salesReportsSlice = createSlice({
    name: 'salesReports',
    initialState,
    reducers: {
        setFromDate: (state, action) => {
            state.fromDate = action.payload;
        },
        setToDate: (state, action) => {
            state.toDate = action.payload;
        },
        setSelectedBranch: (state, action) => {
            state.selectedBranch = action.payload;
        },
        setFilters: (state, action) => {
            const { fromDate, toDate, selectedBranch } = action.payload;
            if (fromDate !== undefined) state.fromDate = fromDate;
            if (toDate !== undefined) state.toDate = toDate;
            if (selectedBranch !== undefined) state.selectedBranch = selectedBranch;
        },
        // Snapshots the current filters as "applied" — call on Apply/Search click.
        applyFilters: (state) => {
            state.appliedFromDate = state.fromDate;
            state.appliedToDate = state.toDate;
            state.appliedBranch = state.selectedBranch;
        },
        resetFilters: (state) => {
            state.fromDate = null;
            state.toDate = null;
            state.selectedBranch = null;
            state.appliedFromDate = null;
            state.appliedToDate = null;
            state.appliedBranch = null;
            state.salesData = [];
            state.summary = {};
            state.summaryCards = {};
            state.summaryModeTooltips = {};
            state.paymentModes = [];
            state.orgName = '';
            state.branchTitle = '';
            state.error = null;
        },
        clearSalesData: (state) => {
            state.salesData = [];
            state.summary = {};
            state.summaryCards = {};
            state.summaryModeTooltips = {};
            state.paymentModes = [];
            state.orgName = '';
            state.branchTitle = '';
        },
        clearError: (state) => {
            state.error = null;
        }
    },
    extraReducers: (builder) => {
        // Fetch sales report data
        builder
            .addCase(fetchSalesReportData.pending, (state) => {
                state.loading = true;
                state.error = null;
                state.salesData = [];
            })
            .addCase(fetchSalesReportData.fulfilled, (state, action) => {
                state.loading = false;
                const { flatBills, flatSummary, summaryCards, summaryModeTooltips, paymentModes } = buildSalesReportView(action.payload?.bills, action.payload?.summary || {});
                state.salesData = flatBills;
                state.summary = flatSummary;
                state.summaryCards = summaryCards;
                state.summaryModeTooltips = summaryModeTooltips;
                state.paymentModes = paymentModes;
                state.orgName = action.payload?.org_name || '';
                state.branchTitle = action.payload?.branch_title || '';
            })
            .addCase(fetchSalesReportData.rejected, (state, action) => {
                state.loading = false;
                state.error = action.payload;
                state.salesData = [];
                state.summary = {};
                state.summaryCards = {};
                state.summaryModeTooltips = {};
                state.paymentModes = [];
            });
    }
});

// Export actions
export const {
    setFromDate,
    setToDate,
    setSelectedBranch,
    setFilters,
    applyFilters,
    resetFilters,
    clearSalesData,
    clearError
} = salesReportsSlice.actions;

// Selectors
export const selectSalesReports = (state) => state.salesReports;
export const selectSalesData = (state) => state.salesReports.salesData;
export const selectSummary = (state) => state.salesReports.summary;
export const selectSummaryCards = (state) => state.salesReports.summaryCards;
export const selectSummaryModeTooltips = (state) => state.salesReports.summaryModeTooltips;
export const selectPaymentModes = (state) => state.salesReports.paymentModes;
export const selectLoading = (state) => state.salesReports.loading;
export const selectSelectedBranch = (state) => state.salesReports.selectedBranch;
export const selectFromDate = (state) => state.salesReports.fromDate;
export const selectToDate = (state) => state.salesReports.toDate;
export const selectAppliedFromDate = (state) => state.salesReports.appliedFromDate;
export const selectAppliedToDate = (state) => state.salesReports.appliedToDate;
export const selectAppliedBranch = (state) => state.salesReports.appliedBranch;
export const selectError = (state) => state.salesReports.error;
export const selectOrgName = (state) => state.salesReports.orgName;
export const selectBranchTitle = (state) => state.salesReports.branchTitle;

export default salesReportsSlice.reducer;
